import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, Eye, EyeOff, Loader2, KeyRound, RefreshCw, Check } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/cn';

type AuthMode = 'login' | 'verification' | 'forgot-password' | 'reset-password';

const passwordRequirements = [
  { regex: /.{8,}/, label: 'At least 8 characters' },
  { regex: /[A-Z]/, label: 'One uppercase letter' },
  { regex: /[a-z]/, label: 'One lowercase letter' },
  { regex: /[0-9]/, label: 'One number' },
];

// Custom DryVox Logo
const DryVoxLogo = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClasses = { sm: 'w-6 h-6', md: 'w-7 h-7', lg: 'w-8 h-8' };
  return (
    <svg viewBox="0 0 32 32" fill="none" className={sizeClasses[size]}>
      <circle cx="16" cy="6" r="4" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <path d="M16 10V26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M10 16H22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M8 28C8 22 12 20 16 26C20 20 24 22 24 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M4 14C5.5 12 5.5 10 4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      <path d="M28 14C26.5 12 26.5 10 28 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    </svg>
  );
};

/**
 * Login Page - Modern Fintech Design
 * 
 * Features:
 * - Matches landing page aesthetic
 * - Modern fintech-style inputs
 * - Animated background with mesh gradients
 * - Glassmorphism card
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    isLoading,
    error,
    clearError,
    needsVerification,
    verificationEmail,
    confirmRegistration,
    resendCode,
    setNeedsVerification,
    forgotPassword,
    confirmForgotPassword,
  } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendingCode, setResendingCode] = useState(false);

  // Forgot password state
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const checkRequirement = (regex: RegExp) => regex.test(newPassword);
  const allRequirementsMet = passwordRequirements.every((req) => checkRequirement(req.regex));

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }
    if (!password) {
      setFormError('Password is required');
      return;
    }

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setFormError(err.message || 'Login failed. Please try again.');
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!verificationCode.trim()) {
      setFormError('Verification code is required');
      return;
    }

    try {
      await confirmRegistration(verificationEmail || email, verificationCode);
      // After verification, try to login
      if (password) {
        await login(verificationEmail || email, password);
        navigate(from, { replace: true });
      } else {
        setFormError('Email verified! Please enter your password to sign in.');
        setNeedsVerification(false);
      }
    } catch (err: any) {
      setFormError(err.message || 'Verification failed. Please try again.');
    }
  };

  const handleResendCode = async () => {
    setResendingCode(true);
    try {
      await resendCode(verificationEmail || email);
      setFormError('');
      // Show success message briefly
    } catch (err: any) {
      setFormError(err.message || 'Failed to resend code.');
    } finally {
      setResendingCode(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    clearError();

    if (!forgotEmail.trim()) {
      setFormError('Email is required');
      return;
    }

    try {
      await forgotPassword(forgotEmail);
      setAuthMode('reset-password');
      setSuccessMessage('Check your email for the reset code.');
    } catch (err: any) {
      setFormError(err.message || 'Failed to send reset code.');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    clearError();

    if (!resetCode.trim()) {
      setFormError('Reset code is required');
      return;
    }
    if (!newPassword) {
      setFormError('New password is required');
      return;
    }
    if (!allRequirementsMet) {
      setFormError('Password does not meet all requirements');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setFormError('Passwords do not match');
      return;
    }

    try {
      await confirmForgotPassword(forgotEmail, resetCode, newPassword);
      setSuccessMessage('Password reset successful! You can now sign in.');
      setAuthMode('login');
      setEmail(forgotEmail);
      setPassword('');
      setForgotEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      setFormError(err.message || 'Failed to reset password.');
    }
  };

  // Determine current mode based on needsVerification and authMode
  const currentMode = needsVerification ? 'verification' : authMode;

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background - Same as Landing */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-[#030712] to-[#030712]" />
        
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 20% 40%, rgba(56, 189, 248, 0.15) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 70% 60%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
              radial-gradient(ellipse 50% 30% at 50% 80%, rgba(20, 184, 166, 0.12) 0%, transparent 50%)
            `
          }}
        />

        <motion.div
          animate={{ y: [0, -30, 0], x: [0, 15, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.08) 0%, transparent 70%)' }}
        />
        <motion.div
          animate={{ y: [0, 20, 0], x: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(20, 184, 166, 0.06) 0%, transparent 70%)' }}
        />

        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-10"
        >
          <Link to="/" className="inline-flex flex-col items-center gap-4 group">
            <motion.div 
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-xl shadow-cyan-500/25 text-white">
                <DryVoxLogo size="lg" />
              </div>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
            </motion.div>
            <div>
              <h1 className="text-3xl font-display font-bold tracking-tight">
                <span className="text-white">Dry</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">Vox</span>
              </h1>
              <p className="text-sm text-slate-500 mt-1 font-medium">Jarvis for Shipbrokers</p>
            </div>
          </Link>
        </motion.div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={cn(
            "p-8 rounded-3xl",
            "bg-gradient-to-b from-slate-800/60 to-slate-900/60",
            "backdrop-blur-xl border border-white/10",
            "shadow-2xl shadow-black/40"
          )}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-display font-bold text-white">
              {currentMode === 'verification' && 'Verify your email'}
              {currentMode === 'login' && 'Welcome back'}
              {currentMode === 'forgot-password' && 'Forgot password'}
              {currentMode === 'reset-password' && 'Reset password'}
            </h2>
            <p className="text-slate-400 mt-2">
              {currentMode === 'verification' && `We sent a code to ${verificationEmail || email}`}
              {currentMode === 'login' && 'Sign in to continue your journey'}
              {currentMode === 'forgot-password' && 'Enter your email to receive a reset code'}
              {currentMode === 'reset-password' && `Enter the code sent to ${forgotEmail}`}
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-6"
            >
              <p className="text-sm text-emerald-400 text-center font-medium">
                {successMessage}
              </p>
            </motion.div>
          )}

          {/* Verification Code Form */}
          {currentMode === 'verification' && (
            <form onSubmit={handleVerification} className="space-y-5">
              {/* Verification Code Input */}
              <div className="relative">
                <label 
                  htmlFor="code"
                  className={cn(
                    "absolute left-12 transition-all duration-200 pointer-events-none",
                    focusedField === 'code' || verificationCode
                      ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400"
                      : "top-1/2 -translate-y-1/2 text-slate-400"
                  )}
                >
                  Verification code
                </label>
                <div className="relative">
                  <div className={cn(
                    "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
                    focusedField === 'code' ? "text-teal-400" : "text-slate-500"
                  )}>
                    <KeyRound className="w-5 h-5" />
                  </div>
                  <input
                    id="code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    onFocus={() => setFocusedField('code')}
                    onBlur={() => setFocusedField(null)}
                    className={cn(
                      "w-full h-14 pl-12 pr-4 rounded-xl",
                      "bg-slate-800/50 border-2 border-slate-700/50",
                      "text-white font-medium placeholder-transparent tracking-widest text-center text-lg",
                      "transition-all duration-200",
                      "hover:border-slate-600",
                      "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
                      "focus:ring-4 focus:ring-teal-500/10"
                    )}
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
              </div>

              {/* Error Message */}
              {(error || formError) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-red-500/10 border border-red-500/20"
                >
                  <p className="text-sm text-red-400 text-center font-medium">
                    {error || formError}
                  </p>
                </motion.div>
              )}

              {/* Verify Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: isLoading ? 1 : 1.01 }}
                whileTap={{ scale: isLoading ? 1 : 0.99 }}
                className={cn(
                  "w-full h-14 rounded-xl font-semibold text-base",
                  "bg-gradient-to-r from-teal-500 to-cyan-500",
                  "hover:from-teal-400 hover:to-cyan-400",
                  "shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40",
                  "transition-all duration-300",
                  "flex items-center justify-center gap-3",
                  "disabled:opacity-70 disabled:cursor-not-allowed"
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify & Sign in
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>

              {/* Resend Code */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendingCode}
                  className="text-slate-400 hover:text-teal-400 text-sm font-medium transition-colors inline-flex items-center gap-2"
                >
                  {resendingCode ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Resend code
                </button>
              </div>

              {/* Back to login */}
              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => setNeedsVerification(false)}
                  className="text-slate-500 hover:text-slate-400 text-sm transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            </form>
          )}

          {/* Forgot Password Form */}
          {currentMode === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              {/* Email Input */}
              <div className="relative">
                <label
                  htmlFor="forgotEmail"
                  className={cn(
                    "absolute left-12 transition-all duration-200 pointer-events-none",
                    focusedField === 'forgotEmail' || forgotEmail
                      ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400"
                      : "top-1/2 -translate-y-1/2 text-slate-400"
                  )}
                >
                  Email address
                </label>
                <div className="relative">
                  <div className={cn(
                    "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
                    focusedField === 'forgotEmail' ? "text-teal-400" : "text-slate-500"
                  )}>
                    <Mail className="w-5 h-5" />
                  </div>
                  <input
                    id="forgotEmail"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    onFocus={() => setFocusedField('forgotEmail')}
                    onBlur={() => setFocusedField(null)}
                    className={cn(
                      "w-full h-14 pl-12 pr-4 rounded-xl",
                      "bg-slate-800/50 border-2 border-slate-700/50",
                      "text-white font-medium placeholder-transparent",
                      "transition-all duration-200",
                      "hover:border-slate-600",
                      "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
                      "focus:ring-4 focus:ring-teal-500/10"
                    )}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Error Message */}
              {(error || formError) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-red-500/10 border border-red-500/20"
                >
                  <p className="text-sm text-red-400 text-center font-medium">
                    {error || formError}
                  </p>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: isLoading ? 1 : 1.01 }}
                whileTap={{ scale: isLoading ? 1 : 0.99 }}
                className={cn(
                  "w-full h-14 rounded-xl font-semibold text-base",
                  "bg-gradient-to-r from-teal-500 to-cyan-500",
                  "hover:from-teal-400 hover:to-cyan-400",
                  "shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40",
                  "transition-all duration-300",
                  "flex items-center justify-center gap-3",
                  "disabled:opacity-70 disabled:cursor-not-allowed"
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  <>
                    Send reset code
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>

              {/* Back to login */}
              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => { setAuthMode('login'); setFormError(''); clearError(); }}
                  className="text-slate-500 hover:text-slate-400 text-sm transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            </form>
          )}

          {/* Reset Password Form */}
          {currentMode === 'reset-password' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              {/* Reset Code Input */}
              <div className="relative">
                <label
                  htmlFor="resetCode"
                  className={cn(
                    "absolute left-12 transition-all duration-200 pointer-events-none",
                    focusedField === 'resetCode' || resetCode
                      ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400"
                      : "top-1/2 -translate-y-1/2 text-slate-400"
                  )}
                >
                  Reset code
                </label>
                <div className="relative">
                  <div className={cn(
                    "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
                    focusedField === 'resetCode' ? "text-teal-400" : "text-slate-500"
                  )}>
                    <KeyRound className="w-5 h-5" />
                  </div>
                  <input
                    id="resetCode"
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    onFocus={() => setFocusedField('resetCode')}
                    onBlur={() => setFocusedField(null)}
                    className={cn(
                      "w-full h-14 pl-12 pr-4 rounded-xl",
                      "bg-slate-800/50 border-2 border-slate-700/50",
                      "text-white font-medium placeholder-transparent tracking-widest text-center text-lg",
                      "transition-all duration-200",
                      "hover:border-slate-600",
                      "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
                      "focus:ring-4 focus:ring-teal-500/10"
                    )}
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
              </div>

              {/* New Password Input */}
              <div className="relative">
                <label
                  htmlFor="newPassword"
                  className={cn(
                    "absolute left-12 transition-all duration-200 pointer-events-none z-10",
                    focusedField === 'newPassword' || newPassword
                      ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400"
                      : "top-1/2 -translate-y-1/2 text-slate-400"
                  )}
                >
                  New password
                </label>
                <div className="relative">
                  <div className={cn(
                    "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
                    focusedField === 'newPassword' ? "text-teal-400" : "text-slate-500"
                  )}>
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onFocus={() => setFocusedField('newPassword')}
                    onBlur={() => setFocusedField(null)}
                    className={cn(
                      "w-full h-14 pl-12 pr-12 rounded-xl",
                      "bg-slate-800/50 border-2 border-slate-700/50",
                      "text-white font-medium placeholder-transparent",
                      "transition-all duration-200",
                      "hover:border-slate-600",
                      "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
                      "focus:ring-4 focus:ring-teal-500/10"
                    )}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              {newPassword && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="grid grid-cols-2 gap-2 pt-1"
                >
                  {passwordRequirements.map((req, index) => {
                    const isMet = checkRequirement(req.regex);
                    return (
                      <div
                        key={index}
                        className={cn(
                          'flex items-center gap-2 text-xs transition-colors',
                          isMet ? 'text-emerald-400' : 'text-slate-500'
                        )}
                      >
                        <div
                          className={cn(
                            'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0',
                            isMet ? 'bg-emerald-500/20' : 'bg-slate-700/50'
                          )}
                        >
                          {isMet && <Check className="w-2.5 h-2.5" />}
                        </div>
                        <span>{req.label}</span>
                      </div>
                    );
                  })}
                </motion.div>
              )}

              {/* Confirm New Password Input */}
              <div className="relative">
                <label
                  htmlFor="confirmNewPassword"
                  className={cn(
                    "absolute left-12 transition-all duration-200 pointer-events-none z-10",
                    focusedField === 'confirmNewPassword' || confirmNewPassword
                      ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400"
                      : "top-1/2 -translate-y-1/2 text-slate-400"
                  )}
                >
                  Confirm new password
                </label>
                <div className="relative">
                  <div className={cn(
                    "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
                    focusedField === 'confirmNewPassword' ? "text-teal-400" : "text-slate-500"
                  )}>
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    id="confirmNewPassword"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    onFocus={() => setFocusedField('confirmNewPassword')}
                    onBlur={() => setFocusedField(null)}
                    className={cn(
                      "w-full h-14 pl-12 pr-4 rounded-xl",
                      "bg-slate-800/50 border-2 border-slate-700/50",
                      "text-white font-medium placeholder-transparent",
                      "transition-all duration-200",
                      "hover:border-slate-600",
                      "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
                      "focus:ring-4 focus:ring-teal-500/10"
                    )}
                    autoComplete="new-password"
                  />
                </div>
                {confirmNewPassword && newPassword !== confirmNewPassword && (
                  <p className="text-red-400 text-xs mt-1.5 ml-1">Passwords do not match</p>
                )}
              </div>

              {/* Error Message */}
              {(error || formError) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-red-500/10 border border-red-500/20"
                >
                  <p className="text-sm text-red-400 text-center font-medium">
                    {error || formError}
                  </p>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading || (!allRequirementsMet && newPassword.length > 0)}
                whileHover={{ scale: isLoading ? 1 : 1.01 }}
                whileTap={{ scale: isLoading ? 1 : 0.99 }}
                className={cn(
                  "w-full h-14 rounded-xl font-semibold text-base",
                  "bg-gradient-to-r from-teal-500 to-cyan-500",
                  "hover:from-teal-400 hover:to-cyan-400",
                  "shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40",
                  "transition-all duration-300",
                  "flex items-center justify-center gap-3",
                  "disabled:opacity-70 disabled:cursor-not-allowed"
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  <>
                    Reset password
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>

              {/* Back to login */}
              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => { setAuthMode('login'); setFormError(''); clearError(); }}
                  className="text-slate-500 hover:text-slate-400 text-sm transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            </form>
          )}

          {/* Login Form */}
          {currentMode === 'login' && (
          <>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div className="relative">
              <label 
                htmlFor="email"
                className={cn(
                  "absolute left-12 transition-all duration-200 pointer-events-none",
                  focusedField === 'email' || email
                    ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400"
                    : "top-1/2 -translate-y-1/2 text-slate-400"
                )}
              >
                Email address
              </label>
              <div className="relative">
                <div className={cn(
                  "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
                  focusedField === 'email' ? "text-teal-400" : "text-slate-500"
                )}>
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className={cn(
                    "w-full h-14 pl-12 pr-4 rounded-xl",
                    "bg-slate-800/50 border-2 border-slate-700/50",
                    "text-white font-medium placeholder-transparent",
                    "transition-all duration-200",
                    "hover:border-slate-600",
                    "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
                    "focus:ring-4 focus:ring-teal-500/10"
                  )}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="relative">
              <label 
                htmlFor="password"
                className={cn(
                  "absolute left-12 transition-all duration-200 pointer-events-none z-10",
                  focusedField === 'password' || password
                    ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400"
                    : "top-1/2 -translate-y-1/2 text-slate-400"
                )}
              >
                Password
              </label>
              <div className="relative">
                <div className={cn(
                  "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
                  focusedField === 'password' ? "text-teal-400" : "text-slate-500"
                )}>
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className={cn(
                    "w-full h-14 pl-12 pr-12 rounded-xl",
                    "bg-slate-800/50 border-2 border-slate-700/50",
                    "text-white font-medium placeholder-transparent",
                    "transition-all duration-200",
                    "hover:border-slate-600",
                    "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
                    "focus:ring-4 focus:ring-teal-500/10"
                  )}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setAuthMode('forgot-password'); setFormError(''); clearError(); setForgotEmail(email); }}
                className="text-sm text-slate-400 hover:text-teal-400 transition-colors"
              >
                Forgot password?
              </button>
            </div>

            {/* Error Message */}
            {(error || formError) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-red-500/10 border border-red-500/20"
              >
                <p className="text-sm text-red-400 text-center font-medium">
                  {error || formError}
                </p>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: isLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading ? 1 : 0.99 }}
              className={cn(
                "w-full h-14 rounded-xl font-semibold text-base",
                "bg-gradient-to-r from-teal-500 to-cyan-500",
                "hover:from-teal-400 hover:to-cyan-400",
                "shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40",
                "transition-all duration-300",
                "flex items-center justify-center gap-3",
                "disabled:opacity-70 disabled:cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700/50"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-slate-900/80 text-slate-500">or</span>
            </div>
          </div>

          {/* Sign up link */}
          <div className="text-center">
            <p className="text-slate-400">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-teal-400 hover:text-teal-300 font-semibold transition-colors inline-flex items-center gap-1 group"
              >
                Create one
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </p>
          </div>
          </>
          )}
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-slate-600 text-sm mt-8"
        >
          By signing in, you agree to our{' '}
          <a href="#" className="text-slate-500 hover:text-teal-400 transition-colors">Terms</a>
          {' & '}
          <a href="#" className="text-slate-500 hover:text-teal-400 transition-colors">Privacy</a>
        </motion.p>
      </div>
    </div>
  );
}

export default Login;
